import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getProduct } from '@/data/products';

const CartContext = createContext(null);

// Parse "$25.000 COP" -> 25000
const parsePrice = (str = '') => {
  const digits = String(str).replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

export const formatCOP = (n) =>
  '$' + n.toLocaleString('es-CO') + ' COP';

const STORAGE_KEY = 'jelly_cart_v1';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((slug, shadeIndex = -1, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.slug === slug && it.shadeIndex === shadeIndex);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...prev, { slug, shadeIndex, qty }];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((slug, shadeIndex, qty) => {
    setItems((prev) =>
      prev
        .map((it) => (it.slug === slug && it.shadeIndex === shadeIndex ? { ...it, qty } : it))
        .filter((it) => it.qty > 0)
    );
  }, []);

  const remove = useCallback((slug, shadeIndex) => {
    setItems((prev) => prev.filter((it) => !(it.slug === slug && it.shadeIndex === shadeIndex)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const detailed = useMemo(() => items.map((it) => {
    const p = getProduct(it.slug);
    if (!p) return null;
    const shade = it.shadeIndex >= 0 ? p.shades[it.shadeIndex] : null;
    const unit = parsePrice(p.price);
    return {
      ...it,
      product: p,
      shade,
      unitPrice: unit,
      lineTotal: unit * it.qty,
    };
  }).filter(Boolean), [items]);

  const subtotal = useMemo(
    () => detailed.reduce((sum, it) => sum + it.lineTotal, 0),
    [detailed]
  );
  const count = useMemo(
    () => items.reduce((sum, it) => sum + it.qty, 0),
    [items]
  );

  const value = useMemo(
    () => ({ items: detailed, count, subtotal, open, setOpen, add, setQty, remove, clear }),
    [detailed, count, subtotal, open, add, setQty, remove, clear]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};
